// frontend/src/modules/esign/components/UseTemplateDialog.tsx
//
// "New from template": turn a saved template into a concrete draft
// document. Two entry points share this dialog:
//   - a row-level "Use" action on TemplatesPage (`templateId` preselected,
//     the picker is hidden)
//   - a "New from template" button on DocumentsListPage (no `templateId` —
//     shows a template picker first)
// Once a template is picked/preselected, its roles are loaded and the user
// assigns a concrete name + email (optionally via a "pick from team"
// employee shortcut, best-effort like RecipientsEditor's) to every role —
// both signer AND cc roles need an email, mirroring
// `instantiateTemplate`'s validation. Title + every role assigned with a
// valid name/email gates the submit button; a successful instantiate
// navigates straight into the prepare wizard's review step
// (`/dashboard/esign/:id/prepare`), since a template-born document already
// has recipients + fields and just needs a final look before sending.
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { cn } from '../../../lib/utils';
import { esignTemplatesApi } from '../api';
import type { EsignTemplate, TemplateDetail } from '../types';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const nativeSelectClass =
  'flex h-11 w-full items-center rounded-2xl bg-white/80 dark:bg-white/[0.04] backdrop-blur-md px-4 py-2 ' +
  'text-[13.5px] text-ink dark:text-white shadow-pill dark:shadow-none ring-1 ring-ink/8 dark:ring-white/10 ' +
  'font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40 ' +
  'transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-50';

interface PickableEmployee {
  id: string;
  name: string;
  email: string;
}

interface AssignmentState {
  name: string;
  email: string;
  employee_id: string | null;
}

export interface UseTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Preselect a template — hides the picker (used by TemplatesPage's row-level "Use" action). */
  templateId?: string;
}

export function UseTemplateDialog({ open, onOpenChange, templateId }: UseTemplateDialogProps) {
  const navigate = useNavigate();

  const [templates, setTemplates] = React.useState<EsignTemplate[]>([]);
  const [pickedTemplateId, setPickedTemplateId] = React.useState<string>('');
  const effectiveTemplateId = templateId ?? (pickedTemplateId || undefined);

  const [detail, setDetail] = React.useState<TemplateDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = React.useState(false);

  const [title, setTitle] = React.useState('');
  const [assignments, setAssignments] = React.useState<Record<string, AssignmentState>>({});
  const [employees, setEmployees] = React.useState<PickableEmployee[] | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  // Reset local state whenever the dialog opens; load the picker's options
  // when no template was preselected.
  React.useEffect(() => {
    if (!open) return;
    setTitle('');
    setAssignments({});
    setDetail(null);
    setPickedTemplateId('');
    if (!templateId) {
      esignTemplatesApi.listTemplates({}).then((page) => setTemplates(page.data)).catch(() => setTemplates([]));
    }
  }, [open, templateId]);

  // Best-effort "pick from team" — hidden entirely if HR isn't reachable/permitted.
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    import('../../hr/api')
      .then(({ hrApi }) => hrApi.listEmployees())
      .then((emps) => {
        if (cancelled) return;
        setEmployees(emps.map((e: any) => ({ id: e.id, name: e.name, email: e.email })));
      })
      .catch(() => { if (!cancelled) setEmployees(null); });
    return () => { cancelled = true; };
  }, [open]);

  // Load the picked/preselected template's roles.
  React.useEffect(() => {
    if (!effectiveTemplateId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoadingDetail(true);
    esignTemplatesApi.getTemplate(effectiveTemplateId)
      .then((d) => { if (!cancelled) { setDetail(d); setAssignments({}); } })
      .catch(() => { if (!cancelled) setDetail(null); })
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
  }, [effectiveTemplateId]);

  function updateAssignment(roleId: string, patch: Partial<AssignmentState>) {
    setAssignments((prev) => {
      const current: AssignmentState = prev[roleId] ?? { name: '', email: '', employee_id: null };
      return { ...prev, [roleId]: { ...current, ...patch } };
    });
  }

  const roles = detail?.roles ?? [];
  const canSubmit =
    title.trim().length > 0 &&
    !!effectiveTemplateId &&
    roles.length > 0 &&
    roles.every((r) => {
      const a = assignments[r.id];
      return !!a && a.name.trim().length > 0 && EMAIL_RE.test(a.email.trim());
    });

  async function handleSubmit() {
    if (!effectiveTemplateId || !canSubmit) return;
    setSubmitting(true);
    try {
      const { document } = await esignTemplatesApi.instantiate(effectiveTemplateId, {
        title: title.trim(),
        assignments: roles.map((r) => ({
          roleId: r.id,
          name: assignments[r.id].name.trim(),
          email: assignments[r.id].email.trim(),
          employee_id: assignments[r.id].employee_id,
        })),
      });
      toast.success('Document created from template');
      onOpenChange(false);
      // A template-born document already has recipients + fields — skip the
      // upload/recipients/fields wizard steps and land directly on review.
      navigate(`/dashboard/esign/${document.id}/prepare`, { state: { startStep: 4 } });
    } catch (err: any) {
      toast.error('Failed to create document', { description: err?.message ?? String(err) });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New from template</DialogTitle>
          <DialogDescription>
            Assign a name and email to each role, then create the document.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {!templateId && (
            <label className="block">
              <span className="text-[13px] font-semibold text-ink/70 dark:text-white/70">Template</span>
              <select
                aria-label="Template"
                className={cn(nativeSelectClass, 'mt-1.5')}
                value={pickedTemplateId}
                onChange={(e) => setPickedTemplateId(e.target.value)}
              >
                <option value="" disabled>Choose a template</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>
          )}

          <label className="block">
            <span className="text-[13px] font-semibold text-ink/70 dark:text-white/70">Title</span>
            <Input
              className="mt-1.5"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Offer letter — Alice"
            />
          </label>

          {loadingDetail && (
            <p className="text-[13px] text-ink/50 dark:text-white/50">Loading roles…</p>
          )}

          {!loadingDetail && effectiveTemplateId && roles.length === 0 && (
            <p className="text-[13px] text-ink/50 dark:text-white/50">This template has no roles.</p>
          )}

          <div className="space-y-3">
            {roles.map((r) => {
              const a = assignments[r.id] ?? { name: '', email: '', employee_id: null };
              return (
                <div
                  key={r.id}
                  className="rounded-2xl bg-white/70 dark:bg-white/[0.04] ring-1 ring-ink/8 dark:ring-white/10 p-3 space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[13.5px] text-ink dark:text-white">{r.label}</span>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[11px] font-medium',
                        r.role === 'signer'
                          ? 'bg-coral/15 text-coral'
                          : 'bg-ink/8 dark:bg-white/10 text-ink/60 dark:text-white/60',
                      )}
                    >
                      {r.role === 'signer' ? 'Signer' : 'CC'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Name"
                      aria-label={`${r.label} name`}
                      value={a.name}
                      onChange={(e) => updateAssignment(r.id, { name: e.target.value })}
                      className="flex-1"
                    />
                    <Input
                      placeholder="Email"
                      type="email"
                      aria-label={`${r.label} email`}
                      value={a.email}
                      onChange={(e) => updateAssignment(r.id, { email: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                  {employees && employees.length > 0 && (
                    <select
                      aria-label={`${r.label} — pick from team`}
                      className={nativeSelectClass}
                      value=""
                      onChange={(e) => {
                        const emp = employees.find((x) => x.id === e.target.value);
                        if (emp) updateAssignment(r.id, { name: emp.name, email: emp.email, employee_id: emp.id });
                        e.target.value = '';
                      }}
                    >
                      <option value="" disabled>Pick from team</option>
                      {employees.map((e) => (
                        <option key={e.id} value={e.id}>{e.name} — {e.email}</option>
                      ))}
                    </select>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!canSubmit || submitting} onClick={handleSubmit}>
            {submitting ? 'Creating…' : 'Create document'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
